import { Alert, message } from 'antd';
import * as LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import * as React from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Redirect } from 'react-router-dom';
import { initGoogleAnalytics } from 'src/utils/gaService';
import MaintenanceBanner from 'src/components/common/MaintenanceBanner/MaintenanceBanner';
import './App.css';
import { loadInitialData } from './actions/loadInitialData';
import Loader from './components/common/Loader';
import withAuth from './components/hoc/withAuth';
import SideMenu from './components/sidemenu/SideMenu';
import {
  API_BASE_URL,
  CHARGEBEE_PUBLISHABLE_KEY,
  DEVREV_APP_ID,
  ENVIRONMENT,
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  LOG_ROCKET_KEY,
  SENTRY_KEY,
  SESSION_STACK_KEY,
  SITE_NAME
} from './globals';
import { requestNotificationPermission } from './notifications/setup';
import RouterOutlet from './routing/RouterOutlet';
import intercom from './sessionScript/intercomscript';
import sessions from './sessionScript/sessionStackScript';
// import * as Sentry from '@sentry/browser';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import DiwaliPopup from 'src/components/festivities/DiwaliPopup';
import IdleTime from './IdleTime';
import { setForceLogout } from './actions/authentication';
import { storeClientDeviceLocation } from './api/utils';
import OnlineDetector from './components/OnlineDetector';
import PrivacyPolicy from './components/PrivacyPolicy';
import { IframeApps } from './components/common/persistentIframeRenderer/constants';
import PersistentIframeInjector from './components/common/persistentIframeRenderer/persistentIframeInjector';
import { getLoginRoute } from './routing/utils';
import { getSessionTokenForDevRevUser } from './sessionScript/devRev';
import fullStory from './sessionScript/fullStory';

declare global {
  interface Window {
    SessionStack?: any;
    intercomSettings?: any;
  }
}

const styles = (theme) => ({
  mainDiv: {
    height: '100%',
  },
  contentDiv: {
    height: '100%',
  },
  errorBlock: {
    margin: '15px',
  },
});

class App extends React.PureComponent<any, any> {
  messageContainer;

  constructor(props) {
    super(props);
    this.messageContainer = React.createRef();
  }

async sentryInit(){
  if (ENVIRONMENT!=='dev'){
  Sentry.init({
    dsn:SENTRY_KEY, // your Sentry DSN
    integrations: [
      Sentry.browserTracingIntegration(), // Performance monitoring
       Sentry.replayIntegration({}),
    ],
    tracesSampleRate: 1.0, // Adjust for performance monitoring
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.shipsy\.app/],
    replaysSessionSampleRate: 0.1, // Capture 10% of all sessions for Replay
    replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors
    
  });
}
}

  async componentDidMount() {
    const { authenticated, fetchingData, dispatch } = this.props;
    const accessToken = window.localStorage.getItem('authToken');
    const orgId = window.localStorage.getItem('organisationId');
    if (accessToken && orgId && this.props.authSettings) {
      const lastActionTime = window.localStorage.getItem('lastActionTime');
      if (this.props.authSettings.idle_session_timeout && lastActionTime) {
        if (moment().diff(lastActionTime, 'millisecond') > this.props.authSettings.idle_session_timeout) {
          dispatch(setForceLogout());
        }
      }
    }
    message.config({
      getContainer: () => this.messageContainer.current,
    });
    if (!fetchingData) return;
    if (authenticated) {
      // Request for notification permission
      requestNotificationPermission();

      // LogRocket init — non-blocking, no dependency on master data
      this.initLogRocket();

      // Initial data — fires immediately, no LogRocket gate
      await this.startInitData();
    }

    storeClientDeviceLocation();
    this.initDevRev();
  }

  startInitData = async () => {
    const { dispatch } = this.props;
    const isTestOrg = window.localStorage.getItem('isTestOrg');
    await dispatch(
      loadInitialData({
        firstTimeMasterFetch:
          this.props.location.state && this.props.location.state.first_time_master_fetch,
      })
    );
    this.initSessionStack();
    this.initGoogleAnalytics();
    this.initRaven();
    this.sentryInit();
    process.env.NODE_ENV === 'production' && isTestOrg !== 'true' && this.initFullStory();
  };

  initFullStory = () => {
    const {
      userData: { email, name: userName, id: userId },
      organisationName,
    } = this.props;
    fullStory({
      email,
      userName,
      userId,
      organisationId: window.localStorage.getItem('organisationId'),
      organisationName,
    });
  };

  getMonitoringModules = () => {
    const shipperModules = window.localStorage.getItem('monitoringModules');
    return shipperModules ? JSON.parse(shipperModules) : null;
  };

  initRaven = () => {
    const storage = window.localStorage;
    const userId = storage.getItem('userId');
    const monitoringModules = this.getMonitoringModules();
    const isSentryDisabled = false;
    const orgId = storage.getItem('organisationId');
    // if (!isSentryDisabled) {
    //   Sentry.init({ dsn: SENTRY_KEY, environment: ENVIRONMENT });
    //   Sentry.configureScope((scope) => {
    //     scope.setUser({
    //       orgId,
    //       userId,
    //       email: this.props.userData.email,
    //       name: this.props.userData.name,
    //       username: this.props.userData.username,
    //     });
    //   });
    //   const isLogRocketDisabled = monitoringModules && monitoringModules['disable_log_rocket'];
    //   if (!isLogRocketDisabled) {
    //     LogRocket.getSessionURL((sessionURL) => {
    //       Sentry.configureScope((scope) => {
    //         scope.setExtra('sessionURL', sessionURL);
    //       });
    //     });
    //   }
    // }
  };

  initSessionStack = () => {
    const storage = window.localStorage;
    const userId = storage.getItem('userId');
    const name = `${storage.getItem('organisationId')}_${this.props.userData.username}`;
    const monitoringModules = this.getMonitoringModules();
    const isSessionStackDisabled = monitoringModules && monitoringModules.disable_session_stack;
    if (!isSessionStackDisabled && SESSION_STACK_KEY) {
      sessions('SessionStack', SESSION_STACK_KEY);
      window.SessionStack.identify({
        userId,
        displayName: name,
        email: this.props.userData.email,
      });
    }
  };

  initGoogleAnalytics = () => {
    const storage = window.localStorage;
    const isTestOrg = storage.getItem('isTestOrg');
    if (isTestOrg === 'true') return;
    if (GOOGLE_ANALYTICS_MEASUREMENT_ID) {
      initGoogleAnalytics(GOOGLE_ANALYTICS_MEASUREMENT_ID, {
        ...this.props.userData,
        organisationId: window.localStorage.getItem('organisationId'),
      });
    }
  };

  // disableOrganisation = () => {
  //   const organisationId = window.localStorage.getItem('organisationId');
  //   const disabledIds = ['domestic',
  //     'demo', 'containertrackinggeneric', 'nitesh', 'kushal',
  //     'jyotsna', 'ashima', 'sahil', 'krishna', '1'];
  //   const index = disabledIds.findIndex(id => (
  //     organisationId === id
  //   ));
  //   return index !== -1 ? true : false;
  // }

  initLogRocket = () => {
    const monitoringModules = this.getMonitoringModules();
    const isLogRocketDisabled = monitoringModules && monitoringModules.disable_log_rocket;
    if (!isLogRocketDisabled && LOG_ROCKET_KEY) {
      const storage = window.localStorage;
      const userId: any = storage.getItem('userId');
      const email: any = storage.getItem('email');
      const username: any = storage.getItem('username');
      const name = `${storage.getItem('organisationId')}_${username}`;
      LogRocket.init(LOG_ROCKET_KEY);
      LogRocket.identify(userId, {
        name,
        email,
      });
      setupLogRocketReact(LogRocket);
    }
  };

  initIntercom = () => {
    const monitoringModules = this.getMonitoringModules();
    const isIntercomDisabled = monitoringModules && monitoringModules.disable_intercom;
    if (isIntercomDisabled) {
      return;
    }
    const storage = window.localStorage;
    window.intercomSettings = {
      app_id: 'ntvhrj4m',
      user_id: storage.getItem('userId'),
      name: this.props.userData.name, // Full name
      email: this.props.userData.email, // Email address
      company: {
        env: ENVIRONMENT,
        baseurl: API_BASE_URL,
        type: `${this.props.userData.type || 'SHIPPER'}_${storage.getItem('organisationId')}`,
        id:
          this.props.userData.shipping_line_id ||
          this.props.userData.cha_id ||
          this.props.userData.ff_id ||
          storage.getItem('organisationId'),
        name:
          this.props.userData.shipping_line_name ||
          this.props.userData.cha_name ||
          this.props.userData.ff_name ||
          storage.getItem('organisationId'),
      },
    };
    intercom();
  };

  initDevRev = async () => {
    const storage = window.localStorage;
    const isDevrevEnabled = storage.getItem('isDevrevEnabled');

    if (isDevrevEnabled === 'true') {
      const userId = storage.getItem('userId');
      const organisationId = storage.getItem('organisationId');
      const accessToken = storage.getItem('authToken');
      const token = await getSessionTokenForDevRevUser(organisationId, userId, accessToken);
      if (token !== undefined && token !== null) {
        (window as any).plugSDK.init({
          app_id: DEVREV_APP_ID,
          session_token: token,
        });
      } else {
        (window as any).plugSDK.init({
          app_id: DEVREV_APP_ID,
        });
      }
    }
  };


  // initFreshWork = () => {
  //   const monitoringModules = this.getMonitoringModules();
  //   const isFreshWorkEnabled = monitoringModules && monitoringModules['disable_freshwork'];
  //   if (isFreshWorkEnabled) {
  //      return;
  //   }
  //   freshwork();
  // };

  initChargebee = () => {
    (global as any).Chargebee.init({
      site: SITE_NAME,
      publishableKey: CHARGEBEE_PUBLISHABLE_KEY,
    });
  };
  
  render() {
    const {
      classes,
      authenticated,
      fetchingData,
      forceLogout,
      bootstrapError,
      passwordExpired,
    } = this.props;
    if (forceLogout) {
      return (
        <Redirect
          to={{
            pathname: '/logout',
          }}
        ></Redirect>
      );
    }
    if (!authenticated) {
      return (window.location.href = getLoginRoute());
    }

    // When the user's password has expired, render a minimal layout — just the
    // message container and RouterOutlet (which pins the user to the forced
    // change-password screen). We skip fetchingData/bootstrapError guards
    // (getMasterData returns 403 EUSS008 in this state) and skip PrivacyPolicy,
    // SideMenu, IdleTime, etc. which depend on master data and don't belong on
    // the expired-password screen.
    if (passwordExpired) {
      return (
        <div className={classes.mainDiv}>
          <div
            style={{ zIndex: 5000, position: 'fixed', top: '10px' }}
            ref={this.messageContainer}
          ></div>
          <div className={classes.contentDiv}>
            <RouterOutlet />
          </div>
        </div>
      );
    }

    if (fetchingData) {
      return <Loader />;
    }
    if (bootstrapError) {
      return <Alert message={bootstrapError} type="error" className={classes.errorBlock} />;
    }

    return (
      <div className={classes.mainDiv}>
        <MaintenanceBanner />
        <div
          style={{ zIndex: 5000, position: 'fixed', top: '10px' }}
          ref={this.messageContainer}
        ></div>
        <div className={classes.contentDiv}>
          {/* {this.initIntercom()} */}
          {this.initChargebee()}
          {/* {this.initFreshWork()} */}
          <RouterOutlet />
          <IdleTime authSettings={this.props.authSettings} />
        </div>
        <SideMenu />
        <PersistentIframeInjector appName={IframeApps.GENERIC_ANALYTICS} topRenderDistance='0px'/>
        <DiwaliPopup />
        <OnlineDetector />
        <PrivacyPolicy />
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { initializing, logoutReducer, bootstrapReducer } = state;
  return {
    forceLogout: logoutReducer.forceLogout,
    passwordExpired: logoutReducer.passwordExpired,
    fetchingData: initializing,
    bootstrapError: bootstrapReducer.bootstrapError,
    userData: state.masterData.userData,
    organisationName: state.masterData.organisationName,
  };
};

const AppStyled = injectSheet(styles)(withAuth(App));
export default withRouter(connect(mapStateToProps)(AppStyled));
